/**
 * Shared types for modifier core functions (Three.js-free).
 *
 * These types define the typed-array-only interface used by both the
 * main-thread wrappers and the modifier worker.
 */

import type { ModifierOverride } from "../../app/storeTypes.ts";
import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import type { FaceProvenance } from "../../modeling/provenance.ts";
import {
  topologyFromTriangles,
  withEdgeCreases,
} from "../../modeling/topology.ts";
import type {
  FaceAttributeTable,
  PolygonTopology,
  SharedTriangleIndices,
} from "../../modeling/types.ts";
import {
  asSharedTriangleIndices,
  type SceneObject,
  type VertexGroup,
} from "../../modeling/types.ts";
import type { VertexProvenance } from "../../wasm/editOps.ts";

/** Typed-array geometry data that can be transferred to/from a worker. */
export interface ModifierGeometry {
  positions: Float32Array;
  indices: SharedTriangleIndices;
  normals: Float32Array | null;
  uvs: Float32Array | null;
  morphArrays: Float32Array[];
  skinIndex: Float32Array | null;
  skinWeight: Float32Array | null;
  sculptDisplacement: Float32Array | null;
  groups: GeometryGroup[];
  /** Morph target metadata to preserve through the pipeline. */
  morphTargetsRelative?: boolean;
  morphNames?: string[];
  /**
   * Per-face attributes evaluated in this geometry's current face domain.
   * Modifier cores should not mutate this directly; `modifierPipeline` remaps
   * it through `faceProvenance` after each topology-changing step.
   */
  faceAttributes?: FaceAttributeTable;
  /**
   * Explicit wire edges as flat [v0, v1, v0, v1, ...] pairs.
   * Only present when the source mesh has wire edges (EDGE_WIRE flag).
   * Set to null by modifiers that retopologise (subsurf, solidify, skin).
   * Propagated (with offset) by mirror, array, and instance.
   *
   * When `topology` is present, this must describe the SAME explicit edge set
   * as `topology.edges` / `topology.edgeFlags` / `topology.edgeCreases`.
   */
  edges: Uint32Array | null;
  /**
   * Per-edge bitfield flags (EDGE_WIRE etc). Length = edges.length / 2.
   * Null when edges is null.
   */
  edgeFlags: Uint8Array | null;
  /**
   * Per-edge subsurf crease weight in [0, 1]. Length = edges.length / 2.
   * Null / undefined when edges is null OR the source mesh carries no
   * non-zero crease — propagated in tandem with `edgeFlags` by mirror /
   * array, and set to null by modifiers that retopologise (subsurf).
   *
   * Consumed by the Loop subdivision kernel to produce piecewise-smooth
   * crease rules (Hoppe 1994). Other modifiers pass this through untouched.
   *
   * Optional (as opposed to `edges` / `edgeFlags`, which are required-nullable)
   * so that modifier cores which never interact with creases can omit the
   * field entirely without introducing mechanical boilerplate everywhere.
   */
  edgeCreases?: Float32Array | null;
  /**
   * Per-vertex skin radius scale factors. Length = positions.length / 3.
   * Multiplied by mod.depth in skinCore to produce the effective tube radius.
   * Null when all vertices use uniform mod.depth (value 1.0).
   * Propagated by mirror and array; consumed and set to null by skin.
   */
  skinRadii: Float32Array | null;
  /**
   * Polygon face topology in the same shared-vertex index space as `positions`.
   * When present, the bake path uses this directly (preserving quads and ngons)
   * instead of reconstructing from triangles. Absent for modifiers that
   * retopologise (subsurf, skin) or when the pipeline was started without
   * topology (rendering path).
   */
  topology?: PolygonTopology;
  /**
   * Optional per-face generator tags for diagnostics. Length = faceCount.
   * Populated by generators that expose face ownership, currently bevel.
   */
  faceSources?: Uint8Array;
  /**
   * Optional per-face owner vertex ids in source-mesh space. Length = faceCount.
   * `u32::MAX` means "no specific owner". Populated for bevel corner-patch faces.
   */
  faceOwnerVertices?: Uint32Array;
  /**
   * Optional step-local face provenance from the modifier input's face domain
   * to this output's face domain. The pipeline consumes it to remap
   * `faceAttributes` and then clears it before the next modifier step.
   */
  faceProvenance?: FaceProvenance;
  /**
   * Vertex provenance — every modifier core sets this so the bake path can
   * reconcile per-vertex attributes (vertex groups, skin radii, sculpt data)
   * onto the baked geometry. Indexed against the SOURCE mesh's vertex space
   * (not the previous modifier step's space) so the attribute reconciliation
   * happens once at the end of the stack against the original source object.
   *
   * - For the initial geometry (built from a SceneObject), this is identity.
   * - For each modifier step, the core produces a provenance from the input's
   *   vertex space to its output's vertex space, then `applyCoreMod` composes
   *   it with the running provenance (which is anchored to the source mesh).
   */
  provenance: VertexProvenance;
}

export interface GeometryGroup {
  start: number;
  count: number;
  materialIndex?: number;
}

/**
 * Resolve the polygon topology to use for face-aware modifier consumers.
 *
 * If the modifier result already carries topology, keep it authoritative and
 * only backfill `edgeCreases` from the parallel field when the topology does
 * not already own them. When topology is absent, reconstruct a triangle-only
 * fallback from the shared-vertex index buffer.
 */
export function resolveModifierGeometryTopology(
  geo: ModifierGeometry,
): PolygonTopology {
  if (!geo.topology) {
    return topologyFromTriangles(asSharedTriangleIndices(geo.indices));
  }
  return withEdgeCreases(
    geo.topology,
    geo.topology.edgeCreases ?? geo.edgeCreases ?? undefined,
  );
}

/**
 * Evaluated target curve data in owner-local space.
 *
 * The frame basis is stored as normalized tangent/normal vectors plus
 * separate scale magnitudes for the normal/binormal directions so target
 * object scaling can widen/narrow the deformation without introducing skew.
 */
export interface ModifierCurvePath {
  positions: Float32Array;
  tangents: Float32Array;
  normals: Float32Array;
  normalScales: Float32Array;
  binormalScales: Float32Array;
  radii: Float32Array;
  cumulativeLengths: Float32Array;
  cyclic: boolean;
}

export interface ModifierEvaluationInput {
  /**
   * Source geometries for modifiers that reference other scene objects.
   * Keyed by the referenced object's display name.
   */
  sourceGeometries?: Record<string, ModifierGeometry>;
  /**
   * Evaluated target-curve paths for the Curve modifier, keyed by the target
   * object's display name.
   */
  curveTargets?: Record<string, ModifierCurvePath>;
  /**
   * Extra dependency key fragment appended to modifier cache keys.
   * Used when a modifier depends on external scene state that does not live
   * directly inside the authored modifier struct.
   */
  cacheKey?: string;
  /**
   * Inverse of the owner's world rotation matrix (column-major 4×4).
   * Used by the instance modifier's "global" orientation to cancel the
   * owner's rotation so instances align to world axes.
   */
  ownerInverseRotation?: number[];
}

export interface ModifierOwnerTransform {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  /** Owner's vertex groups — passed through so modifiers that reference a
   *  group by id (e.g. Instance) can resolve its indices at evaluation time. */
  vertexGroups?: readonly VertexGroup[];
}

/**
 * Validate that a ModifierGeometry has internally consistent buffer sizes.
 * Catches corruption between modifier pipeline steps (e.g. a mirror modifier
 * doubling positions but not indices, or subsurf producing mismatched normals).
 *
 * In dev builds this runs between every modifier step. In production it is
 * stripped by the bundler.
 */
export function validateModifierGeometry(
  geo: ModifierGeometry,
  label?: string,
): void {
  const tag = label ? `[${label}] ` : "";
  if (geo.positions.length % 3 !== 0) {
    throw new Error(
      `${tag}ModifierGeometry: positions.length ${geo.positions.length} not divisible by 3`,
    );
  }
  const vertexCount = geo.positions.length / 3;
  if (geo.normals && geo.normals.length !== geo.positions.length) {
    throw new Error(
      `${tag}ModifierGeometry: normals.length ${geo.normals.length} !== positions.length ${geo.positions.length}`,
    );
  }
  if (geo.uvs && geo.uvs.length !== vertexCount * 2) {
    throw new Error(
      `${tag}ModifierGeometry: uvs.length ${geo.uvs.length} !== vertexCount*2 (${vertexCount * 2})`,
    );
  }
  for (let i = 0; i < geo.indices.length; i++) {
    if (geo.indices[i] >= vertexCount) {
      throw new Error(
        `${tag}ModifierGeometry: indices[${i}] = ${geo.indices[i]} >= vertexCount ${vertexCount}`,
      );
    }
  }
  for (const [mi, morphArr] of geo.morphArrays.entries()) {
    if (morphArr.length !== geo.positions.length) {
      throw new Error(
        `${tag}ModifierGeometry: morphArrays[${mi}].length ${morphArr.length} !== positions.length ${geo.positions.length}`,
      );
    }
  }
  if (geo.skinIndex && geo.skinIndex.length !== vertexCount * 4) {
    throw new Error(
      `${tag}ModifierGeometry: skinIndex.length ${geo.skinIndex.length} !== vertexCount*4 (${vertexCount * 4})`,
    );
  }
  if (geo.skinWeight && geo.skinWeight.length !== vertexCount * 4) {
    throw new Error(
      `${tag}ModifierGeometry: skinWeight.length ${geo.skinWeight.length} !== vertexCount*4 (${vertexCount * 4})`,
    );
  }
  if (
    geo.sculptDisplacement &&
    geo.sculptDisplacement.length !== geo.positions.length
  ) {
    throw new Error(
      `${tag}ModifierGeometry: sculptDisplacement.length ${geo.sculptDisplacement.length} !== positions.length ${geo.positions.length}`,
    );
  }
  if (geo.skinRadii && geo.skinRadii.length !== vertexCount) {
    throw new Error(
      `${tag}ModifierGeometry: skinRadii.length ${geo.skinRadii.length} !== vertexCount ${vertexCount}`,
    );
  }
  if (
    geo.topology &&
    geo.faceSources &&
    geo.faceSources.length !== geo.topology.offsets.length - 1
  ) {
    throw new Error(
      `${tag}ModifierGeometry: faceSources.length ${geo.faceSources.length} !== faceCount ${geo.topology.offsets.length - 1}`,
    );
  }
  if (
    geo.topology &&
    geo.faceOwnerVertices &&
    geo.faceOwnerVertices.length !== geo.topology.offsets.length - 1
  ) {
    throw new Error(
      `${tag}ModifierGeometry: faceOwnerVertices.length ${geo.faceOwnerVertices.length} !== faceCount ${geo.topology.offsets.length - 1}`,
    );
  }
  if (geo.topology && geo.faceAttributes) {
    const faceCount = geo.topology.offsets.length - 1;
    if (
      geo.faceAttributes.materialIndex &&
      geo.faceAttributes.materialIndex.length !== faceCount
    ) {
      throw new Error(
        `${tag}ModifierGeometry: faceAttributes.materialIndex.length ${geo.faceAttributes.materialIndex.length} !== faceCount ${faceCount}`,
      );
    }
    if (
      geo.faceAttributes.shading &&
      geo.faceAttributes.shading.length !== faceCount
    ) {
      throw new Error(
        `${tag}ModifierGeometry: faceAttributes.shading.length ${geo.faceAttributes.shading.length} !== faceCount ${faceCount}`,
      );
    }
  }
  if (geo.topology) {
    const topoEdgeCount = geo.topology.edges.length / 2;
    const expectParallelEdges = topoEdgeCount > 0;
    if (expectParallelEdges && (!geo.edges || !geo.edgeFlags)) {
      throw new Error(
        `${tag}ModifierGeometry: topology carries explicit edges but parallel edge arrays are missing`,
      );
    }
    if (!expectParallelEdges && (geo.edges || geo.edgeFlags)) {
      throw new Error(
        `${tag}ModifierGeometry: topology has no explicit edges but parallel edge arrays are present`,
      );
    }
    if (geo.edges && !equalArrayLike(geo.edges, geo.topology.edges)) {
      throw new Error(
        `${tag}ModifierGeometry: edges diverge from topology.edges`,
      );
    }
    if (
      geo.edgeFlags &&
      !equalArrayLike(geo.edgeFlags, geo.topology.edgeFlags)
    ) {
      throw new Error(
        `${tag}ModifierGeometry: edgeFlags diverge from topology.edgeFlags`,
      );
    }
    const topoCreases = geo.topology.edgeCreases;
    const parallelCreases = geo.edgeCreases ?? undefined;
    if (!equalOptionalArrayLike(parallelCreases, topoCreases)) {
      throw new Error(
        `${tag}ModifierGeometry: edgeCreases diverge from topology.edgeCreases`,
      );
    }
  }
  if (geo.edges !== null && geo.edges !== undefined) {
    if (geo.edges.length % 2 !== 0) {
      throw new Error(
        `${tag}ModifierGeometry: edges.length ${geo.edges.length} not divisible by 2`,
      );
    }
    const edgeCount = geo.edges.length / 2;
    for (let i = 0; i < geo.edges.length; i++) {
      if (geo.edges[i] >= vertexCount) {
        throw new Error(
          `${tag}ModifierGeometry: edges[${i}] = ${geo.edges[i]} >= vertexCount ${vertexCount}`,
        );
      }
    }
    if (
      geo.edgeFlags !== null &&
      geo.edgeFlags !== undefined &&
      geo.edgeFlags.length !== edgeCount
    ) {
      throw new Error(
        `${tag}ModifierGeometry: edgeFlags.length ${geo.edgeFlags.length} !== edgeCount ${edgeCount}`,
      );
    }
  }
}

function equalArrayLike(a: ArrayLike<number>, b: ArrayLike<number>): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function equalOptionalArrayLike(
  a: ArrayLike<number> | undefined,
  b: ArrayLike<number> | undefined,
): boolean {
  if (!a || !b) return a === b;
  return equalArrayLike(a, b);
}

/** Request sent to the modifier worker. */
export interface ModifierWorkerRequest {
  cacheEpoch: number;
  /** Stable cache key for the base geometry across worker requests. */
  baseCacheKey: string;
  geometry: ModifierGeometry;
  owner: ModifierOwnerTransform;
  modifiers: BlendModifier[];
  /** Reduced dependency scene, keyed by object display name. */
  sceneObjectsByName?: ReadonlyMap<string, SceneObject>;
  /**
   * Stable cache keys for dependency objects, keyed by display name.
   * Used by recursive instance/boolean evaluation inside the worker.
   */
  sceneObjectCacheKeys?: ReadonlyMap<string, string>;
  /**
   * Runtime geometry snapshots for dependencies whose authored SceneObject data
   * is not the evaluated surface (currently posed/morphed mannequins).
   * Keyed by object display name, same as `sceneObjectsByName`.
   */
  runtimeSourceGeometries?: Record<string, ModifierGeometry>;
  /** Stable cache keys for `runtimeSourceGeometries`, keyed by display name. */
  runtimeSourceCacheKeys?: ReadonlyMap<string, string>;
  /**
   * Modifier overrides for dependency scene objects, keyed by object id.
   * Scene objects do not carry authored base modifiers on the object itself,
   * so this map is sufficient for recursive dependency stack evaluation.
   */
  modifierOverrides?: ReadonlyMap<string, readonly ModifierOverride[]>;
}

/** Result returned from the modifier worker. */
export interface ModifierWorkerResult {
  geometry: ModifierGeometry;
}
